import React, { useState } from 'react';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';
import {
  MASTER_INDUSTRIES,
  getMasterTemplates,
  getPageTemplates,
  getAiTeachingExamples,
  templateFieldCount,
  templateToColumns,
} from './masterTemplates';


const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'enum', label: 'Dropdown' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'reference', label: 'Reference' },
];

const BLANK_FIELD = {
  name: '',
  type: 'text',
  required: false,
  ref_schema_id: '',
  dedupe: false,
  search: false,
  seq: false,
  seqPrefix: '',
  seqStart: 1,
};

const CreateTableForm = ({
  isInline = false,
  mode = 'page',
  masterOptions = [],
  onCancel,
  onSuccess,
  submitLabel,
  entityId,
}) => {
  const isMaster = mode === 'master';
  const resolvedSubmitLabel =
    submitLabel || (isInline ? (isMaster ? 'Create master table' : 'Create page') : 'Create Table');
  const fieldTypes = isMaster
    ? DATA_TYPES.filter((type) => type.value !== 'reference')
    : DATA_TYPES;

  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState(() => (isInline ? [{ ...BLANK_FIELD }] : []));
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  const [industry, setIndustry] = useState(MASTER_INDUSTRIES[0].value);
  const [loading, setLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const templates = isMaster ? getMasterTemplates(industry) : getPageTemplates(industry);

  const addColumn = () => {
    setColumns([...columns, { ...BLANK_FIELD }]);
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template.id);
    setColumns(templateToColumns(template, masterOptions));
    setAiNote('');
  };

  const selectAiOption = () => {
    if (selectedTemplate === 'ai') return;
    setSelectedTemplate('ai');
    setColumns([{ ...BLANK_FIELD }]);
    setAiNote('');
    setError('');
  };

  const handleIndustryChange = (nextIndustry) => {
    setIndustry(nextIndustry);
    if (selectedTemplate === 'ai') return;
    const nextTemplates = isMaster
      ? getMasterTemplates(nextIndustry)
      : getPageTemplates(nextIndustry);
    const stillVisible = nextTemplates.some((template) => template.id === selectedTemplate);
    if (!stillVisible) {
      applyTemplate(nextTemplates[0]);
    }
  };

  const generateWithAi = async () => {
    const description = aiPrompt.trim();
    if (description.length < 8) {
      setError('Describe the page in a bit more detail so the fields can be drafted.');
      setSuccess('');
      return;
    }

    const hasDraft = columns.some((column) => column.name.trim());
    if (hasDraft) {
      const replace = window.confirm('Replace the current fields with the AI draft?');
      if (!replace) return;
    }

    setAiLoading(true);
    setError('');
    setSuccess('');
    setAiNote('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/schemas/generate-draft`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: description,
          mode: isMaster ? 'master' : 'page',
          industry,
          masters: masterOptions.map((master) => ({
            id: master.id,
            name: master.label,
          })),
          examples: getAiTeachingExamples(industry, isMaster ? 'master' : 'page'),
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('AI could not draft fields right now. Try again.');
      }
      if (!response.ok) {
        throw new Error(data.message || 'Failed to draft fields');
      }

      const draft = data.data || {};
      const nextColumns = Array.isArray(draft.fields) ? draft.fields : [];
      if (nextColumns.length === 0) {
        throw new Error('AI did not return any fields. Try adding more detail.');
      }

      if (!tableName.trim() && draft.name) {
        setTableName(draft.name);
      }
      const allowedTypes = new Set(fieldTypes.map((type) => type.value));
      setColumns(
        nextColumns.map((field) => ({
          ...BLANK_FIELD,
          name: field.name || '',
          type: allowedTypes.has(field.type) ? field.type : 'text',
          required: false,
          ref_schema_id: field.type === 'reference' ? (field.ref_schema_id || '') : '',
          dedupe: field.dedupe === true,
          search: field.search === true,
          seq: isMaster && field.seq === true,
          seqPrefix: field.seqPrefix || '',
          seqStart: field.seqStart || 1,
        }))
      );
      setSelectedTemplate('ai');
      const countLabel = `${nextColumns.length} field${nextColumns.length === 1 ? '' : 's'}`;
      const extra = Array.isArray(draft.notes) && draft.notes.length ? ` ${draft.notes.join(' ')}` : '';
      setAiNote(
        `Drafted ${countLabel}${draft.name ? ` for "${draft.name}"` : ''}. Review them below, then ${
          isMaster ? 'create the master table' : 'create the page'
        }.${extra}`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const removeColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const updateColumn = (index, field, value) => {
    const nextColumns = [...columns];
    nextColumns[index] = { ...nextColumns[index], [field]: value };
    if (field === 'type') {
      nextColumns[index].ref_schema_id = value === 'reference' ? (nextColumns[index].ref_schema_id || '') : '';
    }
    if (field === 'seq' && !value) {
      nextColumns[index].seqPrefix = '';
      nextColumns[index].seqStart = 1;
    }
    setColumns(nextColumns);
  };

  const nameLabel = isMaster ? 'Table name' : isInline ? 'Page name' : 'Table Name *';
  const namePlaceholder = isMaster
    ? 'e.g. Students, Employees, Roles'
    : isInline
      ? 'e.g. Attendance, Inventory, Admissions'
      : 'Enter table name (e.g., Student Records)';
  const nameRequiredMessage = isMaster
    ? 'Table name is required'
    : isInline
      ? 'Page name is required'
      : 'Table name is required';
  const fieldRequiredMessage = isInline || isMaster
    ? 'At least one field is required'
    : 'At least one column is required';
  const fieldNameRequiredPrefix = isInline || isMaster ? 'Field' : 'Column';
  const uniqueNamesMessage = isInline || isMaster
    ? 'Field names must be unique'
    : 'Column names must be unique';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!tableName.trim()) {
      setError(nameRequiredMessage);
      return;
    }

    if (columns.length === 0) {
      setError(fieldRequiredMessage);
      return;
    }

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      if (!column.name.trim()) {
        setError(`${fieldNameRequiredPrefix} ${i + 1} name is required`);
        return;
      }
      if (column.type === 'reference' && !column.ref_schema_id) {
        setError(`${fieldNameRequiredPrefix} ${i + 1} must be linked to a master table`);
        return;
      }
    }

    const columnNames = columns.map((col) => col.name.toLowerCase());
    if (new Set(columnNames).size !== columnNames.length) {
      setError(uniqueNamesMessage);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        table_name: tableName.trim(),
        columns: columns.map((col) => {
          const mapped = {
            name: col.name.trim(),
            type: col.type,
            required: col.required,
            dedupe: col.dedupe === true,
            search: col.search === true,
            seq: isMaster && col.seq === true,
          };
          if (col.type === 'reference' && col.ref_schema_id) {
            mapped.ref_schema_id = col.ref_schema_id;
          }
          if (mapped.seq) {
            mapped.seqPrefix = col.seqPrefix || '';
            const start = parseInt(col.seqStart, 10);
            mapped.seqStart = Number.isFinite(start) && start > 0 ? start : 1;
          }
          return mapped;
        }),
        is_master: isMaster,
      };

      if (entityId) {
        payload.entity_id = entityId;
      }

      const response = await fetch(`${API_URL}/schemas`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create table');
      }

      if (onSuccess) {
        onSuccess(data.data);
        return;
      }

      setSuccess('Table created successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isInline) {
    return (
      <div className="builder-card">
        <form onSubmit={handleSubmit} className="builder-form">
          <h3 className="builder-title">
            {isMaster ? 'Create a new master table' : 'Create a new page'}
          </h3>

          <div className="field-block">
            <label htmlFor="tableName">{nameLabel}</label>
            <input
              id="tableName"
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder={namePlaceholder}
              className="builder-input"
              required
            />
          </div>

          <div className="field-block">
            <label htmlFor="template-industry">Industry</label>
            <select
              id="template-industry"
              className="builder-select"
              value={industry}
              onChange={(e) => handleIndustryChange(e.target.value)}
            >
              {MASTER_INDUSTRIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field-block">
            <label>Start from</label>
            <div className="template-grid template-grid-master">
              {templates.map((template) => {
                const count = templateFieldCount(template);
                const card = (
                  <button
                    key={template.id}
                    type="button"
                    className={`template-opt template-opt-rich ${
                      selectedTemplate === template.id ? 'selected' : ''
                    }`}
                    onClick={() => applyTemplate(template)}
                  >
                    <span className="template-opt-title">{template.label}</span>
                    <span className="template-opt-desc">{template.desc}</span>
                    {template.id !== 'blank' && (
                      <span className="template-opt-count">{count} fields</span>
                    )}
                  </button>
                );
                if (template.id !== 'blank') return card;
                return (
                  <React.Fragment key={template.id}>
                    {card}
                    <button
                      type="button"
                      className={`template-opt template-opt-rich ${
                        selectedTemplate === 'ai' ? 'selected' : ''
                      }`}
                      onClick={selectAiOption}
                    >
                      <span className="template-opt-title">Describe with AI</span>
                      <span className="template-opt-desc">
                      Describe what you're tracking in plain language and get a suggested column list.
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            {selectedTemplate === 'ai' && (
              <div className="ai-draft-panel">
                <label htmlFor="ai-draft-prompt">Describe what to create</label>
                <textarea
                  id="ai-draft-prompt"
                  className="builder-input ai-prompt"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={
                    isMaster
                      ? 'e.g. A clinic patient roster with name, date of birth, phone, blood group, and an auto patient ID'
                      : 'e.g. Daily student attendance with class, late flag, leave dates, and approval, linked to the students roster'
                  }
                  maxLength={2000}
                  disabled={aiLoading || loading}
                />
                <div className="ai-draft-actions">
                  <button
                    type="button"
                    className="builder-btn builder-btn-accent"
                    onClick={generateWithAi}
                    disabled={aiLoading || loading || aiPrompt.trim().length < 8}
                  >
                    {aiLoading ? 'Drafting fields...' : 'Generate fields'}
                  </button>
                  {aiNote && <p className="ai-draft-note">{aiNote}</p>}
                </div>
              </div>
            )}
          </div>

          <div className={`field-block ${isMaster ? '' : 'no-seq'}`}>
            <label>Fields</label>
            <p className="builder-modal-sub fields-hint">
              Templates are just a starting point — add, remove, rename or retype any field below
              before creating this {isMaster ? 'table' : 'page'}.
            </p>
            <div className={`field-legend ${isMaster ? 'field-legend-master' : 'field-legend-linked'}`}>
              <span>Field name</span>
              <span>Type</span>
              {!isMaster && <span>Linked to</span>}
              <span className="legend-check" title="Use for duplicate detection on import">
                Dupe
              </span>
              <span className="legend-check" title="Show a search filter for this field on the table">
                Search
              </span>
              <span className="legend-seq" title="Auto-generate as a sequence">
                Seq
              </span>
              <span />
            </div>
            <div className="builder-field-list">
              {columns.map((column, index) => (
                <div key={`${selectedTemplate}-${index}`} className="field-row-block">
                  <div className={`field-row ${isMaster ? 'field-row-master' : 'field-row-linked'}`}>
                    <input
                      type="text"
                      value={column.name}
                      onChange={(e) => updateColumn(index, 'name', e.target.value)}
                      placeholder="Field name"
                      className="builder-input"
                      required
                    />
                    <select
                      value={column.type}
                      onChange={(e) => updateColumn(index, 'type', e.target.value)}
                      className="builder-select"
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {!isMaster && (
                      <select
                        value={column.type === 'reference' ? column.ref_schema_id || '' : ''}
                        onChange={(e) => updateColumn(index, 'ref_schema_id', e.target.value)}
                        className={`builder-select field-ref-select ${
                          column.type === 'reference' ? '' : 'is-inactive'
                        }`}
                        required={column.type === 'reference'}
                        disabled={column.type !== 'reference'}
                        aria-hidden={column.type !== 'reference'}
                        tabIndex={column.type === 'reference' ? 0 : -1}
                      >
                        <option value="">Linked to...</option>
                        {masterOptions.length === 0 ? (
                          <option value="" disabled>
                            No master tables yet
                          </option>
                        ) : (
                          masterOptions.map((master) => (
                            <option key={master.id} value={master.id}>
                              {master.label}
                            </option>
                          ))
                        )}
                      </select>
                    )}
                    <label className="dedupe-check" title="Use this field to detect duplicates">
                      <input
                        type="checkbox"
                        checked={column.dedupe === true}
                        onChange={(e) => updateColumn(index, 'dedupe', e.target.checked)}
                      />
                    </label>
                    <label
                      className="search-check"
                      title="Show a search filter for this field on the table"
                    >
                      <input
                        type="checkbox"
                        checked={column.search === true}
                        onChange={(e) => updateColumn(index, 'search', e.target.checked)}
                      />
                    </label>
                    {isMaster && (
                      <label className="seq-check" title="Auto-generate as a sequence">
                        <input
                          type="checkbox"
                          checked={column.seq === true}
                          onChange={(e) => updateColumn(index, 'seq', e.target.checked)}
                        />
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => removeColumn(index)}
                      className="field-remove-btn"
                      aria-label="Remove field"
                      disabled={columns.length === 1}
                    >
                      ×
                    </button>
                  </div>
                  {isMaster && column.seq && (
                    <div className="seq-config">
                      <input
                        type="text"
                        className="builder-input field-seq-prefix"
                        placeholder="Prefix e.g. ADM-"
                        value={column.seqPrefix || ''}
                        onChange={(e) => updateColumn(index, 'seqPrefix', e.target.value)}
                      />
                      <input
                        type="number"
                        className="builder-input field-seq-start"
                        min={1}
                        value={column.seqStart || 1}
                        onChange={(e) => updateColumn(index, 'seqStart', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addColumn} className="add-field-btn">
              + Add field
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="builder-footer">
            <span className="builder-hint">
              {isMaster
                ? 'Master tables can be referenced by fields on your pages'
                : 'Fields map automatically into your analytics schema'}
            </span>
            <div className="builder-footer-actions">
              {onCancel && (
                <button type="button" onClick={onCancel} className="builder-btn" disabled={loading}>
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={loading || aiLoading || columns.length === 0}
                className="builder-btn builder-btn-accent"
              >
                {loading ? 'Creating...' : resolvedSubmitLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="create-table-form-wrapper">
      <div className="create-table-content">
        <form onSubmit={handleSubmit} className="create-table-form">
          <div className="form-section">
            <h2>Table Information</h2>
            <div className="form-group">
              <label htmlFor="tableNameStandalone">Table Name *</label>
              <input
                id="tableNameStandalone"
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="Enter table name (e.g., Student Records)"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h2>Table Columns</h2>
              <button type="button" onClick={addColumn} className="add-column-btn">
                + Add Column
              </button>
            </div>

            {columns.length === 0 ? (
              <div className="no-columns-message">
                <p>No columns added yet. Click &quot;Add Column&quot; to get started.</p>
              </div>
            ) : (
              <div className="columns-list">
                {columns.map((column, index) => (
                  <div key={index} className="column-item">
                    <div className="column-fields">
                      <div className="form-group">
                        <label>Column Name *</label>
                        <input
                          type="text"
                          value={column.name}
                          onChange={(e) => updateColumn(index, 'name', e.target.value)}
                          placeholder="Enter column name"
                          className="form-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Data Type *</label>
                        <select
                          value={column.type}
                          onChange={(e) => updateColumn(index, 'type', e.target.value)}
                          className="form-select"
                        >
                          {DATA_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={column.required}
                            onChange={(e) => updateColumn(index, 'required', e.target.checked)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">Required</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeColumn(index)}
                        className="remove-column-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-actions">
            {onCancel && (
              <button type="button" onClick={onCancel} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
            )}
            <button type="submit" disabled={loading || columns.length === 0} className="submit-btn">
              {loading ? 'Creating...' : resolvedSubmitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTableForm;
